import { useState, useEffect, useMemo } from "react";
import {
  Modal, ModalHeader, ModalBody,
  Spinner, Input, Button,
} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import {
  addInvoiceComment,
  getInvoiceSignedUrls,
  getInvoiceById,
  formatCurrency,
  INVOICE_STATUSES,
} from "./Api";
import { Previewer } from "../../../../components/DocumentPreviewer/Previewer";
import { getFileExtension, imageTypes, pdfTypes, officeTypes, videoTypes }
  from "../../../../components/DocumentPreviewer/utils";

const STATUS_LOOKUP = INVOICE_STATUSES.reduce((acc, s) => {
  acc[s.value] = s;
  return acc;
}, {});

const getFileTypeFromKey = (keyOrName) => {
  const ext = getFileExtension(keyOrName).toLowerCase();
  if (pdfTypes.includes(ext)) return "pdf";
  if (imageTypes.includes(ext)) return "image";
  if (videoTypes.includes(ext)) return "video";
  if (officeTypes.includes(ext)) return "office";
  return "unknown";
};

const StatusPill = ({ status }) => {
  const cfg = STATUS_LOOKUP[status] || STATUS_LOOKUP.Unpaid;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      borderRadius: 20, padding: "3px 12px", fontSize: "0.74rem", fontWeight: 700,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
      {status}
    </span>
  );
};

const Section = ({ icon, title, badge, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="iv-drawer-section">
      <button type="button" className="iv-drawer-section__header" onClick={() => setOpen((p) => !p)}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FeatherIcon icon={icon} size={14} color="#4F46E5" />
          <span>{title}</span>
          {badge !== undefined && (
            <span className="iv-drawer-section__badge">{badge}</span>
          )}
        </div>
        <FeatherIcon icon={open ? "chevron-up" : "chevron-down"} size={14} color="#94A3B8" />
      </button>
      {open && <div className="iv-drawer-section__body">{children}</div>}
    </div>
  );
};

const InvoiceDetailDrawer = ({
  isOpen,
  onClose,
  invoice,
  canEdit,
  onChange,
  onForward,
  onEdit,
}) => {
  const [signedUrls, setSignedUrls] = useState({});
  const [activeAttachment, setActiveAttachment] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!invoice?._id || !isOpen) return;
    const keys = invoice.attachments || [];
    if (!keys.length) {
      setSignedUrls({});
      setActiveAttachment(null);
      return;
    }
    (async () => {
      const urls = await getInvoiceSignedUrls(keys);
      setSignedUrls(urls);
      setActiveAttachment(keys[0]);
    })();
  }, [invoice?._id, isOpen, invoice?.attachments]); // eslint-disable-line

  const refresh = async () => {
    if (!invoice?._id) return;
    setRefreshing(true);
    try {
      const fresh = await getInvoiceById(invoice._id);
      if (fresh) onChange?.(fresh);
    } catch {
      // toast handled
    } finally {
      setRefreshing(false);
    }
  };

  const handlePostComment = async () => {
    if (!comment.trim()) return;
    setPostingComment(true);
    try {
      const res = await addInvoiceComment(invoice._id, { comment: comment.trim() });
      const newHistory = res?.data?.commentsHistory;
      if (newHistory) {
        onChange?.({ ...invoice, commentsHistory: newHistory });
      }
      setComment("");
      toast.success("Comment added");
    } catch {
      // handled
    } finally {
      setPostingComment(false);
    }
  };

  const timeline = useMemo(() => {
    if (!invoice) return [];
    const items = [];
    (invoice.statusHistory || []).forEach((s) => items.push({
      kind: "status",
      at: s.changedAt,
      from: s.from,
      to: s.to,
      note: s.note,
      by: s.changedBy?.displayName || "Unknown",
      byRole: s.changedBy?.role || "",
    }));
    (invoice.communicationLog || []).forEach((c) => items.push({
      kind: "comm",
      at: c.sentAt,
      channel: c.channel,
      recipients: c.recipients,
      subject: c.subject,
      message: c.message,
      attachmentsIncluded: c.attachmentsIncluded,
      success: c.success,
      by: c.sentBy?.displayName || "Unknown",
      byRole: c.sentBy?.role || "",
    }));
    return items.sort((a, b) => new Date(b.at) - new Date(a.at));
  }, [invoice]);

  if (!isOpen || !invoice) return null;

  const activeUrl = activeAttachment ? signedUrls[activeAttachment] : null;
  const activeType = activeAttachment ? getFileTypeFromKey(activeAttachment) : "unknown";
  const activeName = activeAttachment ? activeAttachment.split("/").pop() : "";

  const fmt = (d, withTime = true) =>
    d ? moment(d).format(withTime ? "MMM D, YYYY · h:mm A" : "MMM D, YYYY") : "—";

  return (
    <>
      <div className={`iv-drawer-backdrop${isOpen ? " is-open" : ""}`} onClick={onClose} />
      <aside className={`iv-drawer${isOpen ? " is-open" : ""}`}>
        <header className="iv-drawer__header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{
                background: "#EEF2FF", color: "#4338CA",
                padding: "2px 8px", borderRadius: 6,
                fontWeight: 700, fontSize: "0.7rem",
              }}>
                #{invoice.invoiceNo || "—"}
              </span>
              <StatusPill status={invoice.status} />
              {invoice.dueDate &&
                invoice.status !== "Paid" &&
                invoice.status !== "Overdue" &&
                new Date(invoice.dueDate) < new Date() && (
                  <span style={{
                    background: "#FEF2F2", color: "#B91C1C",
                    padding: "2px 8px", borderRadius: 6,
                    fontWeight: 700, fontSize: "0.7rem",
                    border: "1px solid #FCA5A5",
                  }}>
                    OVERDUE
                  </span>
                )}
            </div>
            <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }}>
              {invoice.title || "Untitled invoice"}
            </h3>
            <div style={{ fontSize: "0.75rem", color: "#64748B", marginTop: 4 }}>
              From <strong style={{ color: "#334155" }}>{invoice.vendorName || "—"}</strong>
              {invoice.referenceNo ? ` · Ref ${invoice.referenceNo}` : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" className="iv-icon-btn" title="Refresh" onClick={refresh} disabled={refreshing}>
              {refreshing ? <Spinner size="sm" /> : <FeatherIcon icon="refresh-cw" size={14} />}
            </button>
            {canEdit && (
              <button type="button" className="iv-icon-btn" title="Edit" onClick={onEdit}>
                <FeatherIcon icon="edit-2" size={14} />
              </button>
            )}
            {canEdit && (
              <button type="button" className="iv-icon-btn iv-icon-btn--green" title="Forward" onClick={onForward}>
                <FeatherIcon icon="send" size={14} />
              </button>
            )}
            <button type="button" className="iv-icon-btn" title="Close" onClick={onClose}>
              <FeatherIcon icon="x" size={14} />
            </button>
          </div>
        </header>

        <div className="iv-drawer__body">
          {/* Top metrics */}
          <div className="iv-drawer__metrics">
            <div className="iv-drawer__metric">
              <span>Amount</span>
              <strong>{formatCurrency(invoice.amount, invoice.currency)}</strong>
            </div>
            <div className="iv-drawer__metric">
              <span>Invoice Date</span>
              <strong>{fmt(invoice.invoiceDate, false)}</strong>
            </div>
            <div className="iv-drawer__metric">
              <span>Due Date</span>
              <strong>{fmt(invoice.dueDate, false)}</strong>
            </div>
            <div className="iv-drawer__metric">
              <span>Paid At</span>
              <strong>{fmt(invoice.paidAt, false)}</strong>
            </div>
          </div>

          {/* Preview */}
          <Section icon="eye" title="Invoice Preview" badge={invoice.attachments?.length || 0}>
            {(invoice.attachments?.length || 0) === 0 ? (
              <div className="iv-drawer__empty">
                <FeatherIcon icon="file" size={28} color="#CBD5E1" />
                <div>No files attached yet.</div>
                {canEdit && (
                  <button type="button" className="iv-btn iv-btn--ghost" onClick={onEdit}>
                    Upload now
                  </button>
                )}
              </div>
            ) : (
              <div>
                <div className="iv-drawer__preview-strip">
                  {(invoice.attachments || []).map((key) => {
                    const isActive = activeAttachment === key;
                    const url = signedUrls[key];
                    const t = getFileTypeFromKey(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`iv-drawer__preview-thumb${isActive ? " is-active" : ""}`}
                        onClick={() => setActiveAttachment(key)}
                        title={key.split("/").pop()}
                      >
                        {t === "image" && url
                          ? <img src={url} alt="" />
                          : t === "pdf"
                            ? <FeatherIcon icon="file-text" size={20} color="#DC2626" />
                            : <FeatherIcon icon="file" size={20} color="#475569" />}
                      </button>
                    );
                  })}
                </div>
                <div className="iv-drawer__preview-window">
                  {!activeUrl ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                      <Spinner style={{ color: "#A5B4FC" }} />
                    </div>
                  ) : (
                    <Previewer signedUrl={activeUrl} type={activeType} height="380px" width="100%" />
                  )}
                </div>
                {activeAttachment && (
                  <div className="iv-drawer__preview-meta">
                    <div style={{ fontWeight: 600, color: "#334155" }}>{activeName}</div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        type="button"
                        className="iv-btn iv-btn--ghost"
                        onClick={() => setPreviewModalOpen(true)}
                      >
                        <FeatherIcon icon="maximize-2" size={12} /> Full screen
                      </button>
                      {activeUrl && (
                        <a
                          className="iv-btn iv-btn--primary"
                          href={activeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={activeName}
                        >
                          <FeatherIcon icon="download" size={12} /> Download
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* Meta */}
          <Section icon="info" title="Invoice Information">
            <div className="iv-drawer__info-grid">
              <InfoRow label="Property" value={invoice.propertyTitle || invoice.property?.title || "—"} />
              <InfoRow label="Building" value={invoice.property?.buildingName || "—"} />
              <InfoRow label="Category" value={invoice.category?.name || "—"} />
              <InfoRow label="Vendor" value={invoice.vendorName || "—"} />
              <InfoRow label="Vendor Email" value={invoice.vendorEmail || "—"} />
              <InfoRow label="Vendor Phone" value={invoice.vendorPhone || "—"} />
              <InfoRow label="Created By" value={`${invoice.createdBy?.displayName || "—"} (${invoice.createdBy?.role || ""})`} />
              <InfoRow label="Created" value={fmt(invoice.createdAt)} />
            </div>
            {invoice.description && (
              <div style={{ marginTop: 12 }}>
                <div style={{
                  fontSize: "0.68rem", color: "#64748B", textTransform: "uppercase",
                  letterSpacing: "0.5px", fontWeight: 700, marginBottom: 4,
                }}>Description</div>
                <div style={{
                  fontSize: "0.85rem", color: "#1E293B",
                  whiteSpace: "pre-wrap", lineHeight: 1.5,
                  background: "#F8FAFC", padding: "10px 12px",
                  border: "1px solid #E2E8F0", borderRadius: 8,
                }}>
                  {invoice.description}
                </div>
              </div>
            )}
          </Section>

          {/* Comments */}
          <Section icon="message-square" title="Comments" badge={invoice.commentsHistory?.length || 0}>
            {canEdit && (
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                <Input
                  type="textarea"
                  rows={2}
                  placeholder="Add a comment for the team…"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  style={{ borderRadius: 8, fontSize: "0.85rem", borderColor: "#E2E8F0" }}
                />
                <button
                  type="button"
                  className="iv-btn iv-btn--primary"
                  disabled={postingComment || !comment.trim()}
                  onClick={handlePostComment}
                  style={{ alignSelf: "flex-end" }}
                >
                  {postingComment ? <Spinner size="sm" /> : <FeatherIcon icon="send" size={12} />}
                  Post
                </button>
              </div>
            )}
            {(invoice.commentsHistory || []).length === 0 ? (
              <div style={{ color: "#94A3B8", fontSize: "0.82rem", textAlign: "center", padding: 12 }}>
                No comments yet.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {invoice.commentsHistory.slice().reverse().map((c, idx) => (
                  <div key={c._id || idx} className="iv-comment">
                    <div className="iv-comment__avatar">
                      {(c.author?.displayName || "?").charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="iv-comment__meta">
                        <strong>{c.author?.displayName || "Unknown"}</strong>
                        <span>{c.author?.role || ""}</span>
                        <span>· {fmt(c.createdAt)}</span>
                      </div>
                      <div className="iv-comment__body">{c.comment}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Unified timeline (status + communications) */}
          <Section icon="activity" title="Activity & Communication Log" badge={timeline.length}>
            {timeline.length === 0 ? (
              <div style={{ color: "#94A3B8", fontSize: "0.82rem", textAlign: "center", padding: 12 }}>
                No activity yet.
              </div>
            ) : (
              <ol className="iv-timeline">
                {timeline.map((t, idx) => (
                  <li key={idx} className="iv-timeline__item">
                    <div className={`iv-timeline__dot iv-timeline__dot--${t.kind === "status" ? "status" : t.channel}`}>
                      <FeatherIcon
                        icon={
                          t.kind === "status"
                            ? "flag"
                            : t.channel === "email"
                              ? "mail"
                              : "message-circle"
                        }
                        size={11}
                        color="#fff"
                      />
                    </div>
                    <div className="iv-timeline__card">
                      <div className="iv-timeline__head">
                        <strong>
                          {t.kind === "status"
                            ? `Status changed to "${t.to}"`
                            : t.channel === "email"
                              ? "Email sent"
                              : "SMS sent"}
                        </strong>
                        <span>{fmt(t.at)}</span>
                      </div>
                      {t.kind === "status" && (
                        <div className="iv-timeline__body">
                          {t.from ? <>From <em>{t.from}</em> → <em>{t.to}</em><br /></> : null}
                          {t.note ? <span style={{ color: "#475569" }}>Note: {t.note}<br /></span> : null}
                          <span style={{ color: "#64748B" }}>By {t.by} ({t.byRole})</span>
                        </div>
                      )}
                      {t.kind === "comm" && (
                        <div className="iv-timeline__body">
                          {t.subject && <div><span>Subject:</span> {t.subject}</div>}
                          {t.message && (
                            <div style={{ color: "#475569", whiteSpace: "pre-wrap", margin: "4px 0" }}>
                              "{t.message}"
                            </div>
                          )}
                          <div style={{ marginTop: 4 }}>
                            <span>Recipients:</span>{" "}
                            {(t.recipients || []).map((r, i) => (
                              <span
                                key={i}
                                style={{
                                  display: "inline-block",
                                  marginRight: 4,
                                  padding: "1px 8px",
                                  borderRadius: 10,
                                  fontSize: "0.7rem",
                                  background: r.success ? "#ECFDF5" : "#FEF2F2",
                                  color: r.success ? "#065F46" : "#991B1B",
                                  border: r.success ? "1px solid #A7F3D0" : "1px solid #FECDD3",
                                }}
                                title={r.error || ""}
                              >
                                {r.target} {r.success ? "✓" : "✕"}
                              </span>
                            ))}
                          </div>
                          {t.attachmentsIncluded > 0 && (
                            <div style={{ color: "#64748B", marginTop: 2 }}>
                              📎 {t.attachmentsIncluded} attachment(s) included
                            </div>
                          )}
                          <div style={{ color: "#64748B", marginTop: 4 }}>By {t.by} ({t.byRole})</div>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Section>
        </div>
      </aside>

      <Modal
        isOpen={previewModalOpen}
        toggle={() => setPreviewModalOpen(false)}
        size="xl"
        centered
        style={{ zIndex: 12000 }}
      >
        <ModalHeader toggle={() => setPreviewModalOpen(false)} style={{ background: "#0F172A", borderBottom: "none" }}>
          <span style={{ color: "white", fontWeight: 700, fontSize: "0.9rem" }}>{activeName}</span>
        </ModalHeader>
        <ModalBody style={{ background: "#0F172A", padding: 0 }}>
          {activeUrl && <Previewer signedUrl={activeUrl} type={activeType} height="78vh" width="100%" />}
        </ModalBody>
      </Modal>
    </>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="iv-drawer__info-row">
    <div className="iv-drawer__info-label">{label}</div>
    <div className="iv-drawer__info-value">{value}</div>
  </div>
);

export { InvoiceDetailDrawer };
